// src/app/shared/ui/layout/BottomNav.tsx
import { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Home, MessageCircle, User, Image } from "lucide-react";
import { hapticFeedback } from "@tma.js/sdk";
import { useViewportInsets } from "@/app/hooks/useViewportInsets";
import { GenerateBottomSheet } from "@/app/shared/ui/GenerateBottomSheet";
import { useLongPressMenu } from "@/app/core/hooks/useLongPress";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "@/app/core/ui/theme/LanguageProvider";
import { useTheme } from "@/app/core/ui/theme/ThemeProvider";

const safeHaptic = (callback: () => void) => {
  try { callback(); } catch {}
};

const ITEM_HEIGHT = 56;

export function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const isHome = location.pathname === "/";
  const isProfile = location.pathname === "/profile";
  const [pressedLink, setPressedLink] = useState<string | null>(null);
  const [isGenerateSheetOpen, setIsGenerateSheetOpen] = useState(false);
  const { contentInsets } = useViewportInsets();

  // 🔍 Отслеживаем фокус на поле ввода главной страницы
  const [isMainInputFocused, setIsMainInputFocused] = useState(false);

  useEffect(() => {
    const checkFocus = () => {
      const active = document.activeElement;
      if (!active) return;
      // Проверяем, является ли активный элемент textarea или input внутри #main-page
      const isTextarea = active.tagName === 'TEXTAREA';
      const isInput = active.tagName === 'INPUT' && (active as HTMLInputElement).type === 'text';
      const insideMainPage = active.closest('#main-page');
      setIsMainInputFocused(!!((isTextarea || isInput) && insideMainPage));
    };

    document.addEventListener('focusin', checkFocus);
    document.addEventListener('focusout', checkFocus);
    checkFocus();

    return () => {
      document.removeEventListener('focusin', checkFocus);
      document.removeEventListener('focusout', checkFocus);
    };
  }, []);

  // ... (остальные переменные и функции без изменений: menuEntries, handleMenuSelect, useLongPressMenu, handleGenerateClick, navLinkStyle, menuBottom, цвета)

  const menuEntries = [
    { labelKey: 'universalChat', icon: MessageCircle },
    { labelKey: 'pinterest', icon: Image },
  ];

  const handleMenuSelect = (item: string | null) => {
    if (!item) {
      navigate("/");
      return;
    }
    switch (item) {
      case "Универсальный чат":
      case "Universal Chat":
        navigate("/text-generation");
        break;
      case "Пинтрест":
      case "Pinterest":
        navigate("/pinterest");
        break;
    }
    safeHaptic(() => hapticFeedback.notificationOccurred("success"));
  };

  const {
    isActivated,
    highlightedIndex,
    buttonRect,
    buttonProps,
  } = useLongPressMenu({
    items: menuEntries.map(e => t(e.labelKey as any)),
    onSelect: handleMenuSelect,
    itemHeight: ITEM_HEIGHT,
  });

  const handleGenerateClick = () => {
    safeHaptic(() => hapticFeedback.impactOccurred("medium"));
    setIsGenerateSheetOpen(true);
  };

  const navLinkStyle = (isActive: boolean, key: string) => ({
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    height: "100%",
    transition: "color 0.2s ease, transform 0.2s ease",
    color: isActive ? "var(--accent)" : "var(--foreground)",
    transform: pressedLink === key ? "scale(0.95)" : "scale(1)",
    willChange: "transform",
    textDecoration: "none",
    gap: "4px",
    userSelect: "none" as const,
    fontWeight: 500,
  });

  const menuBottom = buttonRect ? window.innerHeight - buttonRect.top + 16 : 0;

  const navBackground = isDark
    ? "rgba(18, 18, 30, 0.8)"
    : "rgba(255, 255, 255, 0.75)";
  const navBorderColor = isDark
    ? "rgba(255, 255, 255, 0.15)"
    : "rgba(0, 0, 0, 0.1)";
  const menuBackground = isDark
    ? "rgba(30, 30, 50, 0.9)"
    : "rgba(255, 255, 255, 0.9)";
  const menuBorderColor = isDark
    ? "rgba(255, 255, 255, 0.2)"
    : "rgba(0, 0, 0, 0.08)";

  return (
    <>
      <motion.nav
        initial={{ y: 0, opacity: 1 }}
        animate={{
          y: isMainInputFocused ? 20 : 0,
          opacity: isMainInputFocused ? 0 : 1,
        }}
        transition={{
          duration: isMainInputFocused ? 0 : 0.25,
          ease: "easeInOut",
        }}
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          paddingBottom: `${contentInsets.bottom}px`,
          backgroundColor: navBackground,
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          borderTop: `1px solid ${navBorderColor}`,
          boxShadow: isDark
            ? "0 -8px 20px rgba(0, 0, 0, 0.4)"
            : "0 -4px 12px rgba(0, 0, 0, 0.06)",
          touchAction: "none",
          overscrollBehavior: "none",
          pointerEvents: isMainInputFocused ? "none" : "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-around",
            height: "72px",
            maxWidth: "var(--container-max-width, 512px)",
            margin: "0 auto",
            padding: "0 16px",
          }}
        >
          {/* Кнопка «Главная» */}
          <button
            type="button"
            {...buttonProps}
            onClick={() => { if (!isActivated) navigate("/"); }}
            aria-label={t('main')}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              height: "100%",
              transition: "color 0.2s ease, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), filter 0.3s",
              color: isHome ? "var(--accent)" : "var(--foreground)",
              background: "none",
              border: "none",
              gap: "4px",
              cursor: "pointer",
              transform: isActivated ? "translateY(-10px) scale(1.06)" : "none",
              filter: isActivated ? "drop-shadow(0 6px 10px rgba(0,0,0,0.2))" : "none",
              userSelect: "none",
              WebkitUserSelect: "none",
              fontWeight: 500,
            }}
          >
            <Home
              style={{
                width: "24px",
                height: "24px",
                transition: "transform 0.3s ease",
                transform: isActivated ? "scale(1.15) rotate(8deg)" : "none",
                userSelect: "none",
              }}
            />
            <span style={{ fontSize: "12px", fontWeight: 500 }}>{t('main')}</span>
          </button>

          {/* Центральная кнопка Chats */}
          <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "flex-start", height: "100%" }}>
            <button
              type="button"
              onClick={handleGenerateClick}
              aria-label={t('chats')}
              style={{
                border: "none",
                background: "transparent",
                color: "var(--foreground)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "4px",
                transform: "translateY(-18px)",
                userSelect: "none",
                fontWeight: 500,
              }}
            >
              <div
                style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  background: "linear-gradient(135deg, #8b5cf6, #6366f1)",
                  boxShadow: "0 14px 28px rgba(99, 102, 241, 0.3)",
                }}
              >
                <MessageCircle style={{ width: "24px", height: "24px" }} />
              </div>
              <span style={{ fontSize: "12px", fontWeight: 600 }}>{t('chats')}</span>
            </button>
          </div>

          {/* Третья кнопка Профиль */}
          <Link
            to="/profile"
            onMouseDown={() => setPressedLink("/profile")}
            onMouseUp={() => setPressedLink(null)}
            onMouseLeave={() => setPressedLink(null)}
            aria-label={t('profile')}
            style={navLinkStyle(isProfile, "/profile")}
          >
            <User
              style={{ width: "24px", height: "24px", fill: isProfile ? "currentColor" : "none" }}
            />
            <span style={{ fontSize: "12px", fontWeight: 500 }}>{t('profile')}</span>
          </Link>
        </div>

        {/* Меню с анимацией */}
        <AnimatePresence>
          {isActivated && buttonRect && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 8 }}
              transition={{ type: "spring", stiffness: 500, damping: 30 }}
              style={{
                position: "fixed",
                left: Math.max(16, buttonRect.left + buttonRect.width / 2 - 65),
                bottom: menuBottom,
                zIndex: 100,
                width: 130,
                backgroundColor: menuBackground,
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                borderRadius: 16,
                boxShadow: isDark
                  ? "0 12px 32px rgba(0, 0, 0, 0.5)"
                  : "0 12px 32px rgba(0, 0, 0, 0.15)",
                border: `1px solid ${menuBorderColor}`,
                overflow: "hidden",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "8px 0" }}>
                {menuEntries.map((entry, index) => {
                  const Icon = entry.icon;
                  const isHighlighted = index === highlightedIndex;
                  return (
                    <div
                      key={entry.labelKey}
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 6,
                        color: isHighlighted ? "var(--accent)" : "var(--foreground)",
                        backgroundColor: isHighlighted ? "var(--accent)/10" : "transparent",
                        borderRadius: 12,
                        margin: "2px 8px",
                        transition: "background-color 0.15s ease, color 0.15s ease",
                        userSelect: "none",
                      }}
                    >
                      <Icon size={24} />
                      <span style={{ fontSize: 12, fontWeight: 500, textAlign: "center" }}>
                        {t(entry.labelKey as any)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      <GenerateBottomSheet
        isOpen={isGenerateSheetOpen}
        onClose={() => setIsGenerateSheetOpen(false)}
      />
    </>
  );
}